import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { Lock, MessageCircle, Flame, Users, Compass, KeyRound, ImageDown, Share2, Loader2, ArrowRight, ShieldCheck, Sparkles, CheckCircle2, Circle, X, Palette } from "lucide-react";
import { Crest } from "../components/Media";

const TEAL = "#14A89A";
const PURPLE = "#A855F7";
const GOLD = "#D4AF37";

const CARDS = [
  { key: "intro", icon: Sparkles, color: PURPLE, eyebrow: "Welcome to", title: "Sovereign Signal", tagline: "Own your signal.",
    body: "An encrypted community & creator platform for people building sovereign lives. Share your work, grow your community, and build something that's truly yours. Invite-only and Council-approved." },
  { key: "privacy", icon: Lock, color: TEAL, eyebrow: "Private by design", title: "Encrypted before it leaves your phone",
    body: "Your posts, photos, videos, comments, name and profile picture are locked on your device with keys only members hold. Our servers store sealed data they cannot read. Not even we can read it.",
    points: ["End to end encryption, built in", "A 12 word recovery phrase that only you hold", "Two factor sign in, required for every Council member"] },
  { key: "realms", icon: Compass, color: GOLD, eyebrow: "What people share", title: "Every part of a sovereign life",
    body: "From homesteading and healing to business, art and raising the next generation, every post finds its realm. Follow what moves you, and find the people building it alongside you.",
    points: ["A directory to find collaborators and kindred spirits", "Trades, skills and services, offered member to member", "Real people, every one approved by the Council"] },
  { key: "exchanges", icon: MessageCircle, color: TEAL, eyebrow: "Exchanges", title: "Messages only you two can read",
    body: "Private conversations, photos and videos between two members, end-to-end encrypted with a key that exists only on your two devices. No one else holds it. Not the Council, not the server, not anyone." },
  { key: "beacon", icon: Flame, color: PURPLE, eyebrow: "The Beacon", title: "Your work. Your space. Your terms.",
    body: "Teach, sell or share in a space built entirely around you. Start as an Artist, a Teacher or Freeform, and build something no algorithm can bury and no feed can swallow.",
    points: ["Offer it free or by monthly subscription", "Paid directly to you through Stripe", "Encrypted with your Beacon's own key", "Download everything, anytime. It's yours."] },
  { key: "creators", icon: Palette, color: TEAL, eyebrow: "For Artists & Teachers", title: "Show your art. Teach your gift. Keep it all.",
    body: "Artists get a gallery made for their work, with full-screen zoom, watermarks, sold marks and private collector inquiries. Teachers get courses that unfold step by step, with drip-released lessons, completion certificates and notes that stay private to each student. Almost everything is optional, so use what serves you and shape the rest to fit.",
    points: ["Paid directly to you, with just a 1% platform fee", "Share any piece or teaser publicly with its own link"] },
  { key: "hearth", icon: Users, color: GOLD, eyebrow: "The Hearth", title: "Your people, gathered close",
    body: "Every Beacon comes with a Hearth, a private, encrypted home for your community. Share posts, make announcements, pin what matters and go live together, with no algorithm deciding who gets to see it." },
  { key: "council", icon: ShieldCheck, color: TEAL, eyebrow: "How it's kept", title: "Kept by a Council. Built to last.",
    body: "Membership is by invitation and Council approval, and everything here is privately backed up and recoverable, so what you build is never lost. You won't find us in the App Store, and that's by design. Type SovereignSignal.app straight into your browser, and if you don't have an invite key yet, you can request one there." },
];

const MINT = "#7ED6C3";
const LILAC = "#9B6CF0";
const DomainText = () => <><span className="text-zinc-200">Sovereign</span><span style={{ color: MINT }}>Signal</span><span style={{ color: LILAC }}>.app</span></>;
const Wordmark = () => (
  <div className="flex items-center gap-2.5" data-testid="about-card-wordmark">
    <Crest className="h-9 w-9" />
    <div className="font-body font-light uppercase leading-[1.1] tracking-[0.24em] text-[13px]">
      <div className="text-white">Sovereign</div>
      <div><span style={{ color: MINT }}>Signal</span><span style={{ color: LILAC }}>.app</span></div>
    </div>
  </div>
);

const renderCard = async (node, key) => {
  const dataUrl = await toPng(node, { pixelRatio: 1080 / node.offsetWidth, backgroundColor: "#17132A", cacheBust: true });
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], `sovereign-signal-${key}.png`, { type: "image/png" });
};

const downloadFile = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url; a.download = file.name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const Card = ({ c, refKey, invite, onSave, busy, canShare, nodeRef, selected, onToggle }) => {
  const ref = useRef(null);
  const midRef = useRef(null);
  const fitRef = useRef(null);
  const Icon = c.icon;
  // Every card is exactly 4:5 (1080 x 1350 when saved); text shrinks only as much as needed to fit.
  useLayoutEffect(() => {
    const box = ref.current, mid = midRef.current, inner = fitRef.current;
    const fit = () => {
      let s = box.offsetWidth / 26;
      inner.style.fontSize = `${s}px`;
      while (inner.offsetHeight > mid.clientHeight && s > 7) { s -= 0.25; inner.style.fontSize = `${s}px`; }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    document.fonts?.ready.then(fit);
    return () => ro.disconnect();
  }, []);
  return (
    <section className={`relative transition-shadow duration-200 ${selected ? "ring-2 ring-[#14A89A] ring-offset-4 ring-offset-[#0E0B1A]" : ""}`} data-testid={`about-card-${c.key}`}>
      <div ref={(el) => { ref.current = el; nodeRef(el); }} className="relative overflow-hidden bg-[#17132A] border border-white/15 px-7 py-8 sm:px-9 sm:py-10" style={{ aspectRatio: "4 / 5" }} data-testid={`about-card-frame-${c.key}`}>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-50" style={{ background: c.color }} />
        <div className="absolute -bottom-28 -left-20 w-80 h-80 rounded-full blur-3xl opacity-35" style={{ background: c.color === TEAL ? PURPLE : TEAL }} />
        <div className="relative flex flex-col h-full">
          <div className="shrink-0"><Wordmark /></div>
          <div ref={midRef} className="flex-1 min-h-0 flex flex-col justify-center pt-7 pb-4">
            <div ref={fitRef}>
              <div className="w-[2.9em] h-[2.9em] flex items-center justify-center rounded-full mb-[1.1em]" style={{ background: `${c.color}22`, border: `1px solid ${c.color}66` }}><Icon size="1.35em" style={{ color: c.color }} /></div>
              <div className="font-body text-[0.69em] tracking-[0.3em] uppercase mb-[0.7em]" style={{ color: c.color }}>{c.eyebrow}</div>
              <h2 className="font-display text-[2.1em] text-white leading-tight mb-[0.45em]">{c.title}</h2>
              {c.tagline && <p className="font-display text-[1.4em] italic -mt-[0.2em] mb-[0.6em]" style={{ color: c.color }} data-testid={`about-card-tagline-${c.key}`}>{c.tagline}</p>}
              <p className="font-body text-[1em] text-zinc-300 leading-relaxed">{c.body}</p>
              {c.points && <ul className="mt-[1.1em] space-y-[0.5em]">{c.points.map((p) => (
                <li key={p} className="flex items-start gap-[0.6em] font-body text-[0.9em] text-zinc-200"><span className="mt-[0.55em] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: c.color }} />{p}</li>
              ))}</ul>}
            </div>
          </div>
          <div className="flex items-end justify-between gap-3 border-t border-white/10 pt-4 shrink-0">
            <span className="font-body text-xs"><DomainText /></span>
            {invite && <span className="font-body text-xs text-white flex items-center gap-1.5" data-testid={`about-card-key-${c.key}`}><KeyRound size={12} style={{ color: GOLD }} /> Invite key <span className="font-semibold tracking-wider" style={{ color: GOLD }}>{invite}</span></span>}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <button onClick={() => onSave(ref.current, c.key)} disabled={!!busy} data-testid={`about-save-${c.key}`}
          className="inline-flex items-center gap-1.5 font-body text-xs text-zinc-400 hover:text-white disabled:opacity-40">
          {busy === refKey ? <Loader2 size={13} className="animate-spin" /> : canShare ? <Share2 size={13} /> : <ImageDown size={13} />} {canShare ? "Share this card" : "Save this card as an image"}
        </button>
        <button onClick={() => onToggle(c.key)} data-testid={`about-select-${c.key}`} aria-pressed={selected}
          className={`inline-flex items-center gap-1.5 font-body text-xs px-3 py-1.5 rounded-full border transition-colors ${selected ? "border-[#14A89A] bg-[#14A89A]/15 text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/30"}`}>
          {selected ? <CheckCircle2 size={13} /> : <Circle size={13} />} {selected ? "Selected" : "Select"}
        </button>
      </div>
    </section>
  );
};

export default function PublicAbout() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const invite = (params.get("ref") || params.get("invite") || "").replace(/[^A-Za-z-]/g, "").toUpperCase().slice(0, 40);
  const [busy, setBusy] = useState("");
  const canShare = useMemo(() => {
    try { return !!navigator.canShare && navigator.canShare({ files: [new File([""], "c.png", { type: "image/png" })] }); } catch { return false; }
  }, []);
  const save = async (node, key) => {
    if (!node) return;
    setBusy(key);
    try {
      const file = await renderCard(node, key);
      if (canShare) { try { await navigator.share({ files: [file], title: "Sovereign Signal" }); return; } catch { /* fall back to download */ } }
      downloadFile(file);
    } finally { setBusy(""); }
  };
  const nodes = useRef({});
  const [picked, setPicked] = useState([]);
  const [ready, setReady] = useState(null);
  const toggle = (key) => { setReady(null); setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key])); };
  const selectAll = () => { setReady(null); setPicked(picked.length === CARDS.length ? [] : CARDS.map((c) => c.key)); };
  const keysInOrder = CARDS.map((c) => c.key).filter((k) => picked.includes(k));
  // Two taps on phones: images are made first, then shared on a fresh tap so the share sheet is allowed to open.
  const shareMany = async () => {
    if (ready) {
      try { await navigator.share({ files: ready, title: "Sovereign Signal" }); } catch { /* user closed the sheet */ }
      return;
    }
    setBusy("multi");
    try {
      const files = [];
      for (const k of keysInOrder) files.push(await renderCard(nodes.current[k], k));
      if (canShare && navigator.canShare({ files })) setReady(files);
      else for (const f of files) { downloadFile(f); await new Promise((r) => setTimeout(r, 350)); }
    } finally { setBusy(""); }
  };
  const join = () => navigate(invite ? `/?ref=${invite}` : "/");
  return (
    <div className="min-h-screen bg-[#0E0B1A] text-white" data-testid="public-about">
      <div className={`max-w-md mx-auto px-4 py-10 ${picked.length ? "pb-28" : ""}`}>
        <div className="mb-8">
          <div className="flex items-center gap-2.5 mb-5"><Crest className="h-9 w-9" /><span className="font-heading text-white">Sovereign Signal</span></div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-tight mb-3">What is Sovereign Signal?</h1>
          <p className="font-body text-sm text-zinc-400 leading-relaxed">A quick look inside, one card at a time. Each card is made to be screenshotted or saved as an image and shared anywhere.</p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button onClick={selectAll} data-testid="about-select-all" className="inline-flex items-center gap-1.5 font-body text-xs px-3.5 py-2 rounded-full border border-[#14A89A]/50 text-[#14A89A] hover:bg-[#14A89A]/10 transition-colors">
              {picked.length === CARDS.length ? <><X size={13} /> Clear selection</> : <><CheckCircle2 size={13} /> Select all {CARDS.length}</>}
            </button>
            <span className="font-body text-xs text-zinc-500">or tap Select on the ones you want, then post them together.</span>
          </div>
          {invite && <p className="font-body text-sm text-zinc-300 mt-4 flex items-center gap-2" data-testid="about-invite-banner"><KeyRound size={14} style={{ color: GOLD }} /> You've been invited with the key <span className="font-semibold tracking-wider" style={{ color: GOLD }}>{invite}</span></p>}
        </div>
        <div className="space-y-10">
          {CARDS.map((c) => <Card key={c.key} c={c} refKey={c.key} invite={invite} onSave={save} busy={busy} canShare={canShare}
            nodeRef={(el) => { nodes.current[c.key] = el; }} selected={picked.includes(c.key)} onToggle={toggle} />)}
        </div>
        <div className="mt-12 border border-white/10 bg-[#0A0A0A] p-6 text-center" data-testid="about-cta">
          <h2 className="font-display text-2xl text-white mb-2">Ready to step inside?</h2>
          <p className="font-body text-sm text-zinc-400 mb-5">{invite ? "Your invite key is ready. Request access and the Council will welcome you in." : "Membership is by invitation. Request an invite and the Council will be in touch."}</p>
          <button onClick={join} data-testid="about-request-invite" className="inline-flex items-center gap-2 px-6 py-3 font-body text-sm font-semibold text-[#0A0A0A]" style={{ background: TEAL }}>
            Request an invite <ArrowRight size={15} />
          </button>
          <button onClick={() => navigate("/c")} data-testid="about-read-compass" className="block mx-auto mt-4 font-body text-xs hover:underline" style={{ color: PURPLE }}>Read the Compass, our ethos and how it all works</button>
        </div>
      </div>
      {picked.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0E0B1A]/90 backdrop-blur-md px-4 py-3" data-testid="about-share-bar">
          <div className="max-w-md mx-auto flex items-center justify-between gap-3">
            <span className="font-body text-sm text-zinc-300" data-testid="about-selected-count">{picked.length} of {CARDS.length} selected</span>
            <button onClick={shareMany} disabled={busy === "multi"} data-testid="about-share-selected"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm font-semibold text-[#0A0A0A] disabled:opacity-60" style={{ background: TEAL }}>
              {busy === "multi" ? <><Loader2 size={15} className="animate-spin" /> Preparing</>
                : ready ? <><Share2 size={15} /> Share {picked.length} now</>
                : canShare ? <><Share2 size={15} /> Share {picked.length} {picked.length === 1 ? "card" : "cards"}</>
                : <><ImageDown size={15} /> Save {picked.length} {picked.length === 1 ? "card" : "cards"}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
